import { getTimeSinceDate } from '@airship-platform/airship-patterns';
import { FlexBox } from '@airship-platform/jam-web/components';
import { type JobActivity } from '@airship-platform/jobs-activities-feature/schema';
import Timeline from '@mui/lab/Timeline';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useMemo } from 'react';
import { Link } from 'react-router';
import { type MergedJobActivity } from '../../../../data-access/jobsActivitiesTracking';
import ROUTE_PATHS from '../../../../routes/paths';

interface ActivitiesSummaryProps {
  activities: JobActivity[];
}

const ActivitiesSummary: React.FC<ActivitiesSummaryProps> = ({
  activities,
}) => {
  const internalActivities = useMemo(() => {
    const compressJobsActivities = (activities: JobActivity[]) => {
      const FIVE_MINUTES = 5 * 60 * 1000;

      const result: MergedJobActivity[] = [];

      for (const activity of activities) {
        const last = result[result.length - 1];

        if (
          last &&
          last.activityType === activity.activityType &&
          last.userId === activity.userId
        ) {
          const lastTime = new Date(last.createdAt).getTime();
          const currentTime = new Date(activity.createdAt).getTime();

          if (currentTime - lastTime <= FIVE_MINUTES) {
            last.count += 1;
            continue;
          }
        }

        result.push({
          ...activity,
          count: 1,
        });
      }

      return result;
    };
    const compressedJobsActivities = compressJobsActivities(activities);
    return compressedJobsActivities
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      )
      .slice(0, 3);
  }, [activities]);

  const getActivityMessage = (activity: MergedJobActivity): React.ReactNode => {
    switch (activity.activityType) {
      case 'APPOINTMENT_PROPOSAL_OPTION_TECHNICIAN_ACCEPTED':
      case 'APPOINTMENT_PROPOSAL_OPTION_CUSTOMER_ACCEPTED':
        return (
          <Typography variant="body3">Proposal option accepted</Typography>
        );
      case 'APPOINTMENT_PROPOSAL_FOLLOW_UP_SENT':
        return <Typography variant="body3">Follow up sent</Typography>;
      case 'APPOINTMENT_PROPOSAL_SHARED':
        return <Typography variant="body3">Proposal shared</Typography>;
      case 'APPOINTMENT_PROPOSAL_OPTION_CREATED':
        return (
          <Typography variant="body3">
            {activity.count} Proposal{' '}
            {activity.count === 1 ? 'option was' : 'options were'} created
          </Typography>
        );
      case 'APPOINTMENT_PROPOSAL_CREATED':
        return <Typography variant="body3">Proposal created</Typography>;
      default:
        return null;
    }
  };

  if (activities.length === 0) {
    return null;
  }

  return (
    <FlexBox
      border={(theme) => `1px solid ${theme.palette.gray?.[300]}`}
      borderRadius={3}
      overflow={'auto'}
      p={[2]}
      flexDirection={'column'}
      gap={3}
    >
      <Typography variant="body1">Latest activity</Typography>
      <Timeline sx={{ p: 0, m: 0 }}>
        {internalActivities.map((activity, index) => (
          <TimelineItem sx={{ minHeight: 50 }} key={activity.activityId}>
            <TimelineSeparator>
              <TimelineDot />
              {index !== internalActivities.length - 1 && <TimelineConnector />}
            </TimelineSeparator>
            <TimelineContent sx={{ py: 0 }}>
              <FlexBox gap={1} justifyContent={'space-between'}>
                {getActivityMessage(activity)}
                <Typography variant="body3">
                  {getTimeSinceDate(
                    new Date().toISOString(),
                    new Date(activity.createdAt).toISOString()
                  )}
                </Typography>
              </FlexBox>
            </TimelineContent>
          </TimelineItem>
        ))}
      </Timeline>
      <Button
        color="softGold"
        component={Link}
        to={ROUTE_PATHS.APPOINTMENT_PATH({
          appointmentId: activities.at(0)?.appointmentId ?? '',
          appointmentProposalId: activities.at(0)?.appointmentProposalId ?? '',
          path: 'activity',
        })}
      >
        See all activity
      </Button>
    </FlexBox>
  );
};

export default ActivitiesSummary;
